/**
 * Date-range-pickerin puhdas päivämäärälogiikka (AB-WP6.9): presettien
 * resolvointi, inklusiivinen päivälaskenta ja aktiivisen presetin tunnistus.
 * Ei DOMia, ei kelloa — "tänään" annetaan parametrina (deterministinen
 * testeissä); runtime.ts:n wireDateRange antaa oikean paikallisen päivän.
 *
 * Kaikki laskenta tehdään UTC-keskiyöllä, jotta aikavyöhyke ei siirrä päiviä.
 * Wire-formaatti on YYYY-MM-DD.
 */

export type DateRange = { start: string; end: string };

/** Presetit tunnistusjärjestyksessä (ensimmäinen täsmäävä voittaa). */
export const PRESET_KEYS = [
  'today', 'yesterday', 'thisWeek', 'lastWeek', 'last7days', 'last30days',
  'thisMonth', 'lastMonth', 'thisQuarter', 'lastQuarter', 'yearToDate',
];

/**
 * Viikon alku on maanantai (ISO-8601, WP-SF6). `last7days` ei ole sama asia:
 * se on liukuva seitsemän päivää, ei ma–su — ja viikkoperusteisessa työssä
 * (tuntikirjanpito, vuorolistat, viikkoraportointi) juuri se ero on koko juttu.
 */
function mondayOffset(dt: Date): number {
  return (dt.getUTCDay() + 6) % 7;
}

function parse(d: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return null;
  const t = Date.parse(d + 'T00:00:00Z');
  return Number.isNaN(t) ? null : new Date(t);
}

function fmt(dt: Date): string {
  const y = dt.getUTCFullYear();
  const m = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const d = String(dt.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** UTC-päivämäärä (vuosi, 0-pohjainen kk, päivä) → wire-string; JS normalisoi. */
function iso(y: number, m: number, d: number): string {
  return fmt(new Date(Date.UTC(y, m, d)));
}

/** Lisää n päivää wire-stringiin (n voi olla negatiivinen). */
export function addDays(date: string, n: number): string {
  const dt = parse(date);
  if (!dt) return date;
  dt.setUTCDate(dt.getUTCDate() + n);
  return fmt(dt);
}

/** Resolvoi preset-avain [start,end]-väliksi "tänään"-ankkuria vasten; null jos ei preset. */
export function resolvePreset(preset: string, today: string): DateRange | null {
  const t = parse(today);
  if (!t) return null;
  const y = t.getUTCFullYear();
  const m = t.getUTCMonth();
  switch (preset) {
    case 'today':
      return { start: today, end: today };
    case 'yesterday': {
      const yd = addDays(today, -1);
      return { start: yd, end: yd };
    }
    case 'thisWeek': {
      const start = addDays(today, -mondayOffset(t));
      return { start, end: addDays(start, 6) };
    }
    case 'lastWeek': {
      const start = addDays(today, -mondayOffset(t) - 7);
      return { start, end: addDays(start, 6) };
    }
    case 'last7days':
      return { start: addDays(today, -6), end: today };
    case 'last30days':
      return { start: addDays(today, -29), end: today };
    case 'thisMonth':
      return { start: iso(y, m, 1), end: iso(y, m + 1, 0) };
    case 'lastMonth':
      return { start: iso(y, m - 1, 1), end: iso(y, m, 0) };
    case 'thisQuarter': {
      const q = Math.floor(m / 3);
      return { start: iso(y, q * 3, 1), end: iso(y, q * 3 + 3, 0) };
    }
    case 'lastQuarter': {
      const q = Math.floor(m / 3) - 1;
      const yy = q < 0 ? y - 1 : y;
      const qq = (q + 4) % 4;
      return { start: iso(yy, qq * 3, 1), end: iso(yy, qq * 3 + 3, 0) };
    }
    case 'yearToDate':
      return { start: iso(y, 0, 1), end: today };
    default:
      return null;
  }
}

/** Inklusiivinen päivien määrä (end − start + 1); 0 jos jompikumpi tyhjä/virheellinen tai käänteinen. */
export function daysBetween(start: string, end: string): number {
  const s = parse(start);
  const e = parse(end);
  if (!s || !e) return 0;
  const diff = Math.round((e.getTime() - s.getTime()) / 86_400_000);
  return diff < 0 ? 0 : diff + 1;
}

/** Tunnista [start,end]-väliä vastaava preset; 'custom' jos molemmat asetettu muttei preset; '' jos tyhjä. */
export function detectPreset(start: string, end: string, today: string): string {
  if (!start || !end) return '';
  for (const key of PRESET_KEYS) {
    const r = resolvePreset(key, today);
    if (r && r.start === start && r.end === end) return key;
  }
  return 'custom';
}
